import type { EarTrainingClip, EarTrainingNote } from '../types/earTraining';
import {
  anchorDoMidiForClip,
  midiToSolfegeToken,
  parseSolfegeToken,
  solfegeTokenToMidiNear,
} from './earTrainingSolfegeTokens';
import {
  orderSolfegePaletteTokens,
  SOLFEGE_PALETTE_ORDER_MAJOR,
} from './earTrainingSolfegeOrder';

/** Full palette order for major (chromatic from do). */
export const SOLFEGE_PALETTE_ORDER = SOLFEGE_PALETTE_ORDER_MAJOR;

/** Hidden unless the current melody answer needs them. */
export const SOLFEGE_OBSCURE = new Set<string>(['di', 'ra', 'ri', 'si', 'li']);

/** Default visible syllables (le before la). */
export const SOLFEGE_PALETTE_CORE = [
  'do',
  're',
  'me',
  'mi',
  'fa',
  'fi',
  'sol',
  'te',
  'le',
  'la',
  'ti',
] as const;

/** @deprecated Use buildPaletteForClip — kept for tests/docs. */
export const SOLFEGE_PALETTE = [...SOLFEGE_PALETTE_ORDER];

/** Palette for a clip: core syllables, octave variants used in the melody, obscure extras. */
export function buildPaletteForClip(clip: EarTrainingClip): string[] {
  const answer = getClipSolfegeAnswer(clip);
  const tokens = new Set<string>(answer);

  for (const base of SOLFEGE_PALETTE_CORE) tokens.add(base);
  for (const token of answer) {
    const { syllable } = parseSolfegeToken(token);
    if (SOLFEGE_OBSCURE.has(syllable)) tokens.add(syllable);
  }

  return orderSolfegePaletteTokens(tokens, clip);
}

export function isObscureSyllable(s: string): boolean {
  return SOLFEGE_OBSCURE.has(parseSolfegeToken(s).syllable);
}

/** Notes for playing the user's solfege answer at the clip tempo (stored midi, transpose 0). */
export function buildUserAnswerPlaybackNotes(
  clip: EarTrainingClip,
  userTokens: readonly string[]
): EarTrainingNote[] | null {
  if (userTokens.length === 0) return null;
  const anchor = anchorDoMidiForClip(clip);
  const notes: EarTrainingNote[] = [];
  for (let i = 0; i < userTokens.length; i++) {
    const ref = clip.notes[Math.min(i, clip.notes.length - 1)]!;
    const refSounding = ref.midi + clip.transposeSemitones;
    const sounding = solfegeTokenToMidiNear(userTokens[i]!, clip, refSounding, anchor);
    if (sounding === null) return null;
    notes.push({
      midi: sounding - clip.transposeSemitones,
      beats: ref.beats,
    });
  }
  return notes;
}

export function getClipSolfegeAnswer(clip: EarTrainingClip): string[] {
  const transpose = clip.transposeSemitones;
  const fifths = clip.keySignatureFifths;
  const anchorDo = anchorDoMidiForClip(clip);
  return clip.notes.map(n => {
    const midi = n.midi + transpose;
    return normalizeSyllable(
      midiToSolfegeToken(midi, clip.tonic, clip.mode, fifths, anchorDo)
    );
  });
}

/** First-note syllable name (ignoring octave marks) for starting-note selection. */
export function firstNoteSyllable(clip: EarTrainingClip): string {
  const answer = getClipSolfegeAnswer(clip);
  return parseSolfegeToken(answer[0] ?? 'do').syllable;
}

export function normalizeSyllable(s: string): string {
  return s.trim().toLowerCase();
}

export type SolfegeCheckResult = {
  correct: boolean;
  message: string;
};

export function checkSolfegeAnswer(
  user: readonly string[],
  expected: readonly string[]
): SolfegeCheckResult {
  const u = user.map(normalizeSyllable);
  const e = expected.map(normalizeSyllable);

  if (u.length === 0) {
    return {
      correct: false,
      message: 'Add solfege syllables for each note, then check your answer.',
    };
  }

  if (u.length !== e.length) {
    return {
      correct: false,
      message: `Not quite — this melody has ${e.length} note${e.length === 1 ? '' : 's'}, but you entered ${u.length}.`,
    };
  }

  const wrongAt: number[] = [];
  for (let i = 0; i < e.length; i++) {
    if (u[i] !== e[i]) wrongAt.push(i + 1);
  }

  if (wrongAt.length === 0) {
    return { correct: true, message: 'Correct! Your solfege matches the melody.' };
  }

  if (wrongAt.length === 1) {
    return {
      correct: false,
      message: `Not quite — syllable ${wrongAt[0]} doesn't match. Listen again or adjust your answer.`,
    };
  }

  return {
    correct: false,
    message: `Not quite — ${wrongAt.length} syllables don't match. Keep listening and try again.`,
  };
}
